/** @typedef {import('headlight-configurable-application').Pict} Pict */

const PictSectionInputExtension = require('pict-section-form').PictInputExtensionProvider;

class PrecompiledFileSelectorInputHandler extends PictSectionInputExtension
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;
	}

	/**
	 * Generates the HTML ID for a select input element.
	 *
	 * @param {string} pInputHTMLID - The HTML ID of the input element.
	 * @return {string} - The generated HTML ID for the select input element.
	 */
	getToggleInputHTMLID(pInputHTMLID)
	{
		return `#TOGGLE-FOR-${pInputHTMLID}`;
	}

	getHiddenInputHTMLID(pInputHTMLID)
	{
		return `#PREVIOUS-VALUE-${pInputHTMLID}`;
	}

	getDisplayInputHTMLID(pInputHTMLID)
	{
		return `#DISPLAY-FOR-${pInputHTMLID}`;
	}

	getToggleableContainerHTMLID(pInputHTMLID)
	{
		return `#TOGGLE-CONTAINER-FOR-${pInputHTMLID}`;
	}

	getClearButtonHTMLID(pInputHTMLID)
	{
		return `#CLEAR-BUTTON-FOR-${pInputHTMLID}`;
	}

	/**
	 * Handles the change event for the data in the select input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The new value of the input.
	 * @param {string} pHTMLSelector - The HTML selector of the input.
	 * @param {string} pTransactionGUID - The transaction GUID for the event dispatch.
	 * @return {any} - The result of the super.onDataChange method.
	 */
	onDataChange(pView, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		// get current visible value
		const tmpInputElements = this.pict.ContentAssignment.getElement('#' + pInput.Macro.RawHTMLID);
		if (!Array.isArray(tmpInputElements) || tmpInputElements.length < 1)
		{
			return false;
		}
		const tmpInputElement = tmpInputElements[0];
		if (!(tmpInputElement instanceof HTMLInputElement))
		{
			return false;
		}
		const currentValue = tmpInputElement.value;
		// update hidden input value
		const tmpHiddenElements = this.pict.ContentAssignment.getElement('#' + pInput.Macro.RawHTMLID);
		if (!Array.isArray(tmpHiddenElements) || tmpHiddenElements.length < 1)
		{
			return false;
		}
		const tmpHiddenElement = tmpHiddenElements[0];
		if (!(tmpHiddenElement instanceof HTMLInputElement))
		{
			return false;
		}
		tmpInputElement.value = '0';
		if (parseInt(currentValue) > 0)
		{
			// we don't clear values here, so we can remember the previous value
			tmpHiddenElement.value = currentValue;
		}
		this.handleDisplayInputValue(pInput, currentValue);
		// update the clear button visibility
		this.handleClearButtonVisbility(pInput, currentValue);

		// update visible input value
		return super.onDataChange(pView, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}

	/**
	 * Handles the visibility of the clear button based on the input value.
	 *
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value to be set.
	 */
	handleClearButtonVisbility(pInput, pValue)
	{
		const tmpClearButtonElements = this.pict.ContentAssignment.getElement(this.getClearButtonHTMLID(pInput.Macro.RawHTMLID));
		if (Array.isArray(tmpClearButtonElements) && tmpClearButtonElements.length > 0)
		{
			const tmpClearButtonElement = tmpClearButtonElements[0];
			if (tmpClearButtonElement instanceof HTMLButtonElement)
			{
				tmpClearButtonElement.style.visibility = pValue && Number(pValue) !== 0 && !isNaN(Number(pValue)) ? 'visible' : 'hidden';
			}
		}
	}

	/**
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value to be set.
	 * @param {boolean} [setInputVisibility=false] - Whether to set the input visibility.
	 */
	async handleInputValue(pInput, pValue, setInputVisibility = false)
	{
		const tmpInputElements = this.pict.ContentAssignment.getElement('#' + pInput.Macro.RawHTMLID);
		if (Array.isArray(tmpInputElements) && tmpInputElements.length > 0)
		{
			const tmpInputElement = tmpInputElements[0];
			if (tmpInputElement instanceof HTMLDivElement)
			{
				if (setInputVisibility)
				{
					this.pict.ContentAssignment.setAttribute(tmpInputElement, 'visibility', 'visible');
				}
				else
				{
					this.pict.ContentAssignment.setAttribute(tmpInputElement, 'visibility', 'hidden');
				}
				// set the input value to the ID
				this.pict.ContentAssignment.assignContent(tmpInputElement, pValue);
			}
		}
	}

	/**
	 * Handles the display of the input value in the Pict provider.
	 *
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value to be displayed.
	 * @return {Promise<void>} - A promise that resolves when the display is updated.
	 */
	async handleDisplayInputValue(pInput, pValue)
	{
		const tmpDisplayElements = this.pict.ContentAssignment.getElement(this.getDisplayInputHTMLID(pInput.Macro.RawHTMLID));
		if (Array.isArray(tmpDisplayElements) && tmpDisplayElements.length > 0)
		{
			const tmpDisplayElement = tmpDisplayElements[0];
			if (tmpDisplayElement instanceof HTMLDivElement)
			{
				if (pValue && Number(pValue) !== 0 && !isNaN(Number(pValue)))
				{
					// look up the observation to get the Name and Description
					const tmpObservation = await this.pict.providers.ObservationProvider.getObservationByID(pValue);
					if (tmpObservation)
					{
						const tmpName = tmpObservation.Name || `Unnamed Observation #${pValue}`;
						const tmpDescription = tmpObservation.Description || 'No description available';
						// set the input value to the ID and Name in a predefined format
						const tmpValue = `<span class="tag has-background-text-90 mr-2">#${pValue}</span> <strong class="mr-1">${tmpName}</strong> - <em class="ml-1">${tmpDescription}</em>`;
						this.pict.ContentAssignment.assignContent(tmpDisplayElement, tmpValue);
					}
				}
				else
				{
					this.pict.ContentAssignment.assignContent(tmpDisplayElement, '');
				}
			}
		}
	}

	/**
	 * Initializes the input element for the Pict provider select input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pRow - The row object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The input value.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {string} pTransactionGUID - The transaction GUID for the event dispatch.
	 * @return {boolean} - Returns true if the input element is successfully initialized, false otherwise.
	 */
	onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		this.handleInputValue(pInput, pValue, true);
		this.handleDisplayInputValue(pInput, pValue);
		this.handleClearButtonVisbility(pInput, pValue);
		return super.onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}

	/**
	 * Marshals data to the form for the given input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pRow - The row object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value to be marshaled.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {string} pTransactionGUID - The transaction GUID for the event dispatch.
	 * @return {boolean} - Returns true if the value is successfully marshaled to the form, otherwise false.
	 */
	onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		this.handleInputValue(pInput, pValue, true);
		this.handleDisplayInputValue(pInput, pValue);
		this.handleClearButtonVisbility(pInput, pValue);
		return super.onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}

	/**
	 * Handles the data request event for a select input in the PictProviderInputSelect class.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value object.
	 * @param {string} pHTMLSelector - The HTML selector object.
	 * @return {any} - The result of the onDataRequest method.
	 */
	onDataRequest(pView, pInput, pValue, pHTMLSelector)
	{
		const tmpInputNumericValue = this.pict.ContentAssignment.readContent(pInput.Macro.RawHTMLID);
		this.pict.ContentAssignment.assignContent(pHTMLSelector, tmpInputNumericValue);
		pView.dataChanged(pInput.Hash);
		return super.onDataRequest(pView, pInput, tmpInputNumericValue, pHTMLSelector);
	}
}

module.exports = PrecompiledFileSelectorInputHandler;
